// Vue
import { computed, ComputedRef, Ref } from 'vue'

// VueUse
import { useBreakpoints } from '@vueuse/core'

// Custom Bootstrap V5 breakpoints to match the modified SCSS breakpoints
const customBreakpoints = {
  xs: 0,
  sm: 576,
  md: 1200,
  lg: 1200,
  xl: 1200,
  xxl: 1400,
}

/**
 * A composable function to determine the current screen size and breakpoints.
 * Utilizes VueUse's `useBreakpoints` with custom breakpoints matching SCSS.
 *
 * @returns {object} An object containing reactive properties for various breakpoints,
 * computed screen sizes, and a mobile indicator.
 * @property {Ref<boolean>} isScreenSM - Indicates if the screen is greater than 'sm'.
 * @property {Ref<boolean>} isScreenMD - Indicates if the screen is greater than 'md'.
 * @property {Ref<boolean>} isScreenLG - Indicates if the screen is greater than 'lg'.
 * @property {Ref<boolean>} isScreenXL - Indicates if the screen is greater than 'xl'.
 * @property {Ref<boolean>} isScreenXXL - Indicates if the screen is greater than 'xxl'.
 * @property {ComputedRef<string[]>} screenSizes - Computed array of active breakpoint sizes, from 'xs' to 'xxl'.
 * @property {ComputedRef<boolean>} isMobile - Indicates if the current screen is considered mobile ('md' or smaller).
 */
const useScreenSize = (): {
  isScreenSM: Ref<boolean>
  isScreenMD: Ref<boolean>
  isScreenLG: Ref<boolean>
  isScreenXL: Ref<boolean>
  isScreenXXL: Ref<boolean>
  screenSizes: ComputedRef<string[]>
  isMobile: ComputedRef<boolean>
} => {
  const breakpoints = useBreakpoints(customBreakpoints)

  const isScreenXS = breakpoints.greater('xs')
  const isScreenSM = breakpoints.greater('sm')
  const isScreenMD = breakpoints.greater('md')
  const isScreenLG = breakpoints.greater('lg')
  const isScreenXL = breakpoints.greater('xl')
  const isScreenXXL = breakpoints.greater('xxl')

  // Array of breakpoint classes
  const screenSizes = computed<string[]>(() => {
    const sizes: string[] = []
    if (isScreenXXL.value) sizes.push('xxl')
    if (isScreenXL.value) sizes.push('xl')
    if (isScreenLG.value) sizes.push('lg')
    if (isScreenMD.value) sizes.push('md')
    if (isScreenSM.value) sizes.push('sm')
    sizes.push('xs')
    return sizes
  })

  // We consider tablets as mobile based on the modified breakpoints
  const isMobile = computed<boolean>(() => {
    return isScreenXS.value && !isScreenXL.value
  })

  return {
    isScreenSM,
    isScreenMD,
    isScreenLG,
    isScreenXL,
    isScreenXXL,
    screenSizes,
    isMobile,
  }
}

export default useScreenSize
